import {
  findQueryTermRanges,
  type QueryTermRange,
} from '@streamlain/search-extract';

/** Marking and snippet windows over the shared search word rule. */
function markRanges(text: string, ranges: readonly QueryTermRange[]) {
  let marked = '';
  let cursor = 0;
  for (const [start, end] of ranges) {
    marked += `${text.slice(cursor, start)}<b>${text.slice(start, end)}</b>`;
    cursor = end;
  }
  return marked + text.slice(cursor);
}

/** Marks every matched term; text the index already marked stays as is. */
export function markQueryTerms(
  text: string | undefined,
  query: string
): string | undefined {
  if (!text || text.includes('<b>')) return text;
  return markRanges(text, findQueryTermRanges(text, query));
}

type SnippetWindow = { before: number; after: number };

function focusRanges(
  text: string,
  ranges: readonly QueryTermRange[],
  { before, after }: SnippetWindow
): string | undefined {
  const first = ranges[0];
  if (!first) return undefined;
  const start = Math.max(0, first[0] - before);
  const end = Math.min(text.length, first[1] + after);
  const inside = ranges
    .filter(([from, to]) => from >= start && to <= end)
    .map(([from, to]): QueryTermRange => [from - start, to - start]);
  return `${start > 0 ? '...' : ''}${markRanges(
    text.slice(start, end),
    inside
  )}${end < text.length ? '...' : ''}`;
}

/**
 * A window around the first match with every match inside it marked, or
 * `undefined` when no term occurs, so callers never show an unexplained hit.
 */
export function focusQueryTerms(
  text: string,
  query: string,
  window: SnippetWindow
): string | undefined {
  return focusRanges(text, findQueryTermRanges(text, query), window);
}

/** Same window for text an index already marked with `<b>` spans. */
export function focusMarkedText(
  marked: string,
  window: SnippetWindow
): string | undefined {
  const ranges: QueryTermRange[] = [];
  let text = '';
  for (const part of marked.split(/(<b>.*?<\/b>)/su)) {
    if (part.startsWith('<b>') && part.endsWith('</b>')) {
      const inner = part.slice(3, -4);
      ranges.push([text.length, text.length + inner.length]);
      text += inner;
    } else {
      text += part;
    }
  }
  return focusRanges(text, ranges, window);
}
